<!--
  Gestione Account — single-page list with modals (redesigned 2026-06-04).
  Replaces /accounts (legacy list) + /accounts/:id (dettagli) + /new-account
  (new) + the dropped edit route. All four flows live here as modals.

  Desktop only. Visual language coherent with dashboard / statistics /
  settings / messaggi.

  Audience: Admin + SuperAdmin.
-->

<div class="accounts-page">
  <!-- ─── Sticky header: title + search + role filter + Nuovo button ── -->
  <header class="accounts-header">
    <div class="accounts-header__titles">
      <h1 class="accounts-header__title">👥 Gestione Account</h1>
      <p class="accounts-header__subtitle">
        @if (filteredAccounts.length > 0) {
          <strong>{{ filteredAccounts.length }}</strong> di
          {{ accounts.length }} account
        } @else if (loading) {
          Caricamento…
        } @else {
          Nessun account registrato
        }
      </p>
    </div>

    <div class="accounts-header__controls">
      <div class="accounts-header__search">
        <div class="search-wrap">
          <input
            type="text"
            class="search-input"
            placeholder="Cerca per nome, email, telefono…"
            [(ngModel)]="searchText"
            (ngModelChange)="onSearchInput()"
            aria-label="Cerca account"
          />
          @if (searchText) {
            <!-- Custom × button (vs the native type=search "X", which is
                 inconsistent across browsers and badly themable). -->
            <button
              type="button"
              class="search-clear"
              (click)="clearSearch()"
              aria-label="Cancella ricerca"
              title="Cancella ricerca"
            >
              ×
            </button>
          }
        </div>
      </div>

      <div class="accounts-header__role-filter">
        <select
          class="role-select"
          [ngModel]="roleFilter"
          (ngModelChange)="setRoleFilter($event)"
          aria-label="Filtra per ruolo"
        >
          <option [ngValue]="null">Tutti i ruoli</option>
          @for (role of roles; track role.code) {
            <option [ngValue]="role.code">{{ role.label }}</option>
          }
        </select>
      </div>

      <button type="button" class="btn btn--primary" (click)="openNew()">
        + Nuovo account
      </button>
    </div>
  </header>

  <!-- ─── Table card ────────────────────────────────────────────── -->
  <section class="accounts-table-card" aria-label="Elenco account">
    @if (loading && !hasAccounts) {
      <!-- Skeleton during the first load -->
      <div class="table-skeleton" aria-busy="true">
        @for (i of [1, 2, 3, 4, 5, 6, 7]; track i) {
          <div class="table-skeleton__row">
            <div class="table-skeleton__avatar"></div>
            <div class="table-skeleton__cells">
              <div class="table-skeleton__line table-skeleton__line--lg"></div>
              <div class="table-skeleton__line table-skeleton__line--sm"></div>
            </div>
          </div>
        }
      </div>
    } @else if (filteredAccounts.length === 0) {
      <!-- Empty state — distinguishes "no accounts at all" from "no filter match" -->
      <div class="accounts-empty">
        <div class="accounts-empty__icon" aria-hidden="true">
          @if (!hasAccounts) {
            📇
          } @else {
            🔎
          }
        </div>
        <p class="accounts-empty__msg">
          @if (!hasAccounts) {
            Nessun account presente. Crea il primo con il bottone
            <strong>+ Nuovo account</strong>.
          } @else if (searchText && roleFilter !== null) {
            Nessun account corrisponde a "<strong>{{ searchText }}</strong
            >" con il filtro ruolo attuale.
          } @else if (searchText) {
            Nessun account corrisponde a "<strong>{{ searchText }}</strong
            >".
          } @else {
            Nessun account per questo ruolo.
          }
        </p>
      </div>
    } @else {
      <div class="prep-table-scroll">
        <table class="accounts-table" role="table">
          <thead>
            <tr>
              <th scope="col" class="col-avatar" aria-label="Avatar"></th>
              <th
                scope="col"
                class="col-name sortable"
                (click)="sort('ac_first_name')"
              >
                Nome
                <span class="sort-icon">{{ sortIcon("ac_first_name") }}</span>
              </th>
              <th
                scope="col"
                class="col-email sortable"
                (click)="sort('ac_email')"
              >
                Email <span class="sort-icon">{{ sortIcon("ac_email") }}</span>
              </th>
              <th
                scope="col"
                class="col-role sortable"
                (click)="sort('ac_user_type_cod')"
              >
                Ruolo
                <span class="sort-icon">{{
                  sortIcon("ac_user_type_cod")
                }}</span>
              </th>
              <th scope="col" class="col-status">Stato</th>
              <th scope="col" class="col-actions" aria-label="Azioni"></th>
            </tr>
          </thead>
          <tbody>
            @for (account of pagedAccounts; track account.account_id) {
              @let badge = roleBadge(account);
              <tr class="accounts-row">
                <td class="col-avatar">
                  <div class="avatar" [attr.data-variant]="badge.variant">
                    {{ initials(account) }}
                  </div>
                </td>
                <td class="col-name">
                  <div class="cell-name">
                    <span class="cell-name__primary">
                      {{ account.ac_last_name }} {{ account.ac_first_name }}
                    </span>
                    @if (account.ac_phone || account.ac_mobile) {
                      <span class="cell-name__phone">
                        📞 {{ account.ac_mobile || account.ac_phone }}
                      </span>
                    }
                  </div>
                </td>
                <td class="col-email">
                  <a
                    class="cell-email"
                    [attr.href]="'mailto:' + account.ac_email"
                  >
                    {{ account.ac_email }}
                  </a>
                </td>
                <td class="col-role">
                  <span class="role-badge" [attr.data-variant]="badge.variant">
                    {{ badge.label }}
                  </span>
                </td>
                <td class="col-status">
                  <span
                    class="status-pill"
                    [class.status-pill--active]="isActive(account)"
                    [class.status-pill--inactive]="!isActive(account)"
                  >
                    {{ isActive(account) ? "Attivo" : "Disattivato" }}
                  </span>
                </td>
                <td class="col-actions">
                  <div class="row-actions">
                    <button
                      type="button"
                      class="icon-btn icon-btn--view"
                      title="Visualizza dettagli"
                      (click)="openView(account)"
                    >
                      📋
                    </button>
                    <button
                      type="button"
                      class="icon-btn icon-btn--edit"
                      title="Modifica"
                      (click)="openEdit(account)"
                    >
                      ✏️
                    </button>
                    <button
                      type="button"
                      class="icon-btn icon-btn--delete"
                      title="Elimina"
                      (click)="openDelete(account)"
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <!-- ─── Pagination ───────────────────────────────────────── -->
      @if (totalPages > 1) {
        <nav class="pagination" aria-label="Paginazione account">
          <button
            type="button"
            class="pagination__btn"
            [disabled]="currentPage === 0"
            (click)="prevPage()"
          >
            ‹ Precedente
          </button>
          <span class="pagination__info">
            Pagina <strong>{{ currentPage + 1 }}</strong> di {{ totalPages }} ·
            <span class="pagination__total"
              >{{ filteredAccounts.length }} account</span
            >
          </span>
          <button
            type="button"
            class="pagination__btn"
            [disabled]="currentPage >= totalPages - 1"
            (click)="nextPage()"
          >
            Successiva ›
          </button>
        </nav>
      }
    }
  </section>

  <!-- ─── Modal layer (renders only when modalMode !== null) ──── -->
  @if (modalMode) {
    <div class="modal-backdrop" (click)="closeModal()">
      <!--
        Inner click stops propagation so clicking the card itself
        doesn't dismiss the modal — only the backdrop closes it.
      -->
      <div class="modal-card" (click)="$event.stopPropagation()">
        <!-- ── VIEW (read-only details) ── -->
        @if (modalMode === "view" && selectedAccount; as account) {
          @let badge = roleBadge(account);
          <header class="modal-header">
            <div
              class="modal-header__avatar"
              [attr.data-variant]="badge.variant"
            >
              {{ initials(account) }}
            </div>
            <div class="modal-header__text">
              <h2 class="modal-header__title">
                {{ account.ac_first_name }} {{ account.ac_last_name }}
              </h2>
              <p class="modal-header__subtitle">
                <span class="role-badge" [attr.data-variant]="badge.variant">{{
                  badge.label
                }}</span>
                <span
                  class="status-pill"
                  [class.status-pill--active]="isActive(account)"
                  [class.status-pill--inactive]="!isActive(account)"
                  >{{ isActive(account) ? "Attivo" : "Disattivato" }}</span
                >
              </p>
            </div>
            <button
              type="button"
              class="modal-close"
              (click)="closeModal()"
              aria-label="Chiudi"
            >
              ×
            </button>
          </header>

          <div class="modal-body modal-body--details">
            <dl class="details-grid">
              <div class="details-grid__row">
                <dt>Email</dt>
                <dd>
                  <a [attr.href]="'mailto:' + account.ac_email">{{
                    account.ac_email
                  }}</a>
                </dd>
              </div>
              <div class="details-grid__row">
                <dt>Telefono</dt>
                <dd>{{ account.ac_phone || "—" }}</dd>
              </div>
              <div class="details-grid__row">
                <dt>Cellulare</dt>
                <dd>{{ account.ac_mobile || "—" }}</dd>
              </div>
              <div class="details-grid__row">
                <dt>Scadenza</dt>
                <dd>{{ formatDate(account.ac_expiration_date) }}</dd>
              </div>
              @if (account.ac_note) {
                <div class="details-grid__row details-grid__row--full">
                  <dt>Note</dt>
                  <dd class="details-grid__note">{{ account.ac_note }}</dd>
                </div>
              }
            </dl>
          </div>

          <footer class="modal-footer">
            <button type="button" class="btn btn--ghost" (click)="closeModal()">
              Chiudi
            </button>
            <button
              type="button"
              class="btn btn--primary"
              (click)="openEdit(account)"
            >
              Modifica
            </button>
          </footer>
        }

        <!-- ── NEW / EDIT (form) ── -->
        @if (modalMode === "new" || modalMode === "edit") {
          <header class="modal-header">
            <h2 class="modal-header__title">
              {{ modalMode === "new" ? "Nuovo account" : "Modifica account" }}
            </h2>
            <button
              type="button"
              class="modal-close"
              (click)="closeModal()"
              aria-label="Chiudi"
            >
              ×
            </button>
          </header>

          <form
            class="modal-body modal-body--form"
            [formGroup]="accountForm"
            (ngSubmit)="save()"
          >
            <div class="form-grid">
              <label class="form-field">
                <span class="form-field__label">Nome *</span>
                <input
                  class="form-field__input"
                  type="text"
                  formControlName="ac_first_name"
                  autocomplete="given-name"
                />
                @if (
                  accountForm.controls["ac_first_name"].touched &&
                  accountForm.controls["ac_first_name"].invalid
                ) {
                  <span class="form-field__error">Il nome è obbligatorio.</span>
                }
              </label>

              <label class="form-field">
                <span class="form-field__label">Cognome *</span>
                <input
                  class="form-field__input"
                  type="text"
                  formControlName="ac_last_name"
                  autocomplete="family-name"
                />
                @if (
                  accountForm.controls["ac_last_name"].touched &&
                  accountForm.controls["ac_last_name"].invalid
                ) {
                  <span class="form-field__error"
                    >Il cognome è obbligatorio.</span
                  >
                }
              </label>

              <label class="form-field form-field--full">
                <span class="form-field__label">Email *</span>
                <input
                  class="form-field__input"
                  type="email"
                  formControlName="ac_email"
                  autocomplete="email"
                />
                @if (
                  accountForm.controls["ac_email"].touched &&
                  accountForm.controls["ac_email"].invalid
                ) {
                  <span class="form-field__error">
                    @if (
                      accountForm.controls["ac_email"].errors?.["required"]
                    ) {
                      L'email è obbligatoria.
                    } @else {
                      Formato email non valido.
                    }
                  </span>
                }
              </label>

              <!--
                🚨 v2.12.0 L.1.A: password field VISIBILE solo per Nuovo
                account. Il design pattern per Modifica resta intatto
                (admin invia reset email via bottone "Reset password"),
                ma per la creazione l'admin deve fornire una password
                iniziale che l'utente cambierà al primo login.
              -->
              @if (modalMode === "new") {
                <label class="form-field form-field--full">
                  <span class="form-field__label">Password iniziale *</span>
                  <input
                    class="form-field__input"
                    type="password"
                    formControlName="ac_password"
                    autocomplete="new-password"
                    placeholder="Minimo 6 caratteri"
                  />
                  @if (
                    accountForm.controls["ac_password"].touched &&
                    accountForm.controls["ac_password"].invalid
                  ) {
                    <span class="form-field__error">
                      @if (
                        accountForm.controls["ac_password"].errors?.["required"]
                      ) {
                        La password è obbligatoria per i nuovi account.
                      } @else if (
                        accountForm.controls["ac_password"].errors?.[
                          "minlength"
                        ]
                      ) {
                        La password deve essere di almeno 6 caratteri.
                      }
                    </span>
                  }
                  <span class="form-field__hint">
                    L'utente la cambierà al primo accesso. Per gli account già
                    esistenti, usa "Reset password" nel modal Modifica.
                  </span>
                </label>
              }

              <label class="form-field">
                <span class="form-field__label">Telefono</span>
                <input
                  class="form-field__input"
                  type="tel"
                  formControlName="ac_phone"
                />
              </label>

              <label class="form-field">
                <span class="form-field__label">Cellulare</span>
                <input
                  class="form-field__input"
                  type="tel"
                  formControlName="ac_mobile"
                />
              </label>

              <label class="form-field">
                <span class="form-field__label">Ruolo *</span>
                <select
                  class="form-field__input"
                  formControlName="ac_user_type_cod"
                >
                  @for (role of roles; track role.code) {
                    @if (role.creatable) {
                      <option [ngValue]="role.code">{{ role.label }}</option>
                    }
                  }
                </select>
              </label>

              <label class="form-field">
                <span class="form-field__label">Scadenza</span>
                <input
                  class="form-field__input"
                  type="date"
                  formControlName="ac_expiration_date"
                />
              </label>

              <label class="form-field form-field--inline form-field--full">
                <input type="checkbox" formControlName="ac_active" />
                <span class="form-field__label">Account attivo</span>
              </label>

              <label class="form-field form-field--full">
                <span class="form-field__label">Note</span>
                <textarea
                  class="form-field__input form-field__input--area"
                  rows="3"
                  formControlName="ac_note"
                ></textarea>
              </label>
            </div>
          </form>

          <footer class="modal-footer modal-footer--split">
            @if (modalMode === "edit" && selectedAccount) {
              <!-- Left-aligned "secondary" action only relevant in edit:
                   trigger the password-reset flow for this account. -->
              <button
                type="button"
                class="btn btn--secondary"
                (click)="triggerPasswordReset()"
                [disabled]="resettingPassword || saving"
                title="Invia email di reset password all'utente"
              >
                @if (resettingPassword) {
                  <span
                    class="btn__spinner btn__spinner--dark"
                    aria-hidden="true"
                  ></span>
                  Invio in corso…
                } @else {
                  🔑 Reset password
                }
              </button>
            }

            <div class="modal-footer__actions">
              <button
                type="button"
                class="btn btn--ghost"
                (click)="closeModal()"
                [disabled]="saving"
              >
                Annulla
              </button>
              <button
                type="button"
                class="btn btn--primary"
                (click)="save()"
                [disabled]="saving || accountForm.invalid"
              >
                @if (saving) {
                  <span class="btn__spinner" aria-hidden="true"></span>
                  Salvataggio…
                } @else {
                  Salva
                }
              </button>
            </div>
          </footer>
        }

        <!-- ── DELETE (confirm) ── -->
        @if (modalMode === "delete" && selectedAccount; as account) {
          <header class="modal-header modal-header--danger">
            <h2 class="modal-header__title">⚠️ Conferma eliminazione</h2>
            <button
              type="button"
              class="modal-close"
              (click)="closeModal()"
              aria-label="Chiudi"
            >
              ×
            </button>
          </header>

          <div class="modal-body modal-body--confirm">
            <p class="confirm-msg">
              Stai per eliminare l'account di
              <strong
                >{{ account.ac_first_name }} {{ account.ac_last_name }}</strong
              >
              (<a [attr.href]="'mailto:' + account.ac_email">{{
                account.ac_email
              }}</a
              >).
            </p>
            <p class="confirm-warn">
              L'azione è irreversibile. L'utente non potrà più accedere a
              Diakonix.
            </p>
          </div>

          <footer class="modal-footer">
            <button
              type="button"
              class="btn btn--ghost"
              (click)="closeModal()"
              [disabled]="deleting"
            >
              Annulla
            </button>
            <button
              type="button"
              class="btn btn--danger"
              (click)="confirmDelete()"
              [disabled]="deleting"
            >
              @if (deleting) {
                <span class="btn__spinner" aria-hidden="true"></span>
                Eliminazione…
              } @else {
                Elimina account
              }
            </button>
          </footer>
        }
      </div>
    </div>
  }
</div>
